import { useState } from "react";
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VerifiedIcon from "@mui/icons-material/Verified";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, formatCurrency } from "@safrater/ui";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";

interface ModuleRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  baseUrl: string;
  webhookUrl: string | null;
  active: boolean;
  subModules: { id: string; key: string; name: string; active: boolean }[];
  _count: { roles: number; licenseTypes: number };
}

interface ModuleFormValues {
  key: string;
  name: string;
  description: string;
  baseUrl: string;
  webhookUrl: string;
  active: boolean;
}

interface DialogState { open: boolean; row?: ModuleRow }

export function ModulesPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMaster = !!user?.isMasterAdmin;
  const [dlg, setDlg] = useState<DialogState>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<ModuleRow | null>(null);
  const [typesDlg, setTypesDlg] = useState<ModuleRow | null>(null);

  const { data } = useQuery({
    queryKey: ["modules"],
    queryFn: async () => (await api.get<ModuleRow[]>("/modules")).data,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["modules"] });
  const createM = useMutation({
    mutationFn: (v: ModuleFormValues) => api.post("/modules", v),
    onSuccess: () => { invalidate(); setDlg({ open: false }); },
  });
  const updateM = useMutation({
    mutationFn: ({ id, v }: { id: string; v: Partial<ModuleFormValues> }) => api.patch(`/modules/${id}`, v),
    onSuccess: () => { invalidate(); setDlg({ open: false }); },
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/modules/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h4">Módulos</Typography>
        {isMaster && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDlg({ open: true })}>
            Novo módulo
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Aplicações registradas no ecossistema, com sub-módulos, papéis e tipos de licença.
      </Typography>
      <Grid container spacing={2}>
        {(data ?? []).map((m) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={m.id}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="h6">{m.name}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" color={m.active ? "success" : "default"} label={m.active ? "Ativo" : "Inativo"} />
                    {isMaster && (
                      <>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => setDlg({ open: true, row: m })}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Desativar">
                          <IconButton size="small" color="error" onClick={() => setConfirmDelete(m)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Stack>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {m.description ?? "—"}
                </Typography>
                <Typography variant="caption" color="text.secondary">Chave</Typography>
                <Typography variant="body2" sx={{ mb: 1, fontFamily: "monospace" }}>{m.key}</Typography>
                <Typography variant="caption" color="text.secondary">URL base</Typography>
                <Typography variant="body2" sx={{ mb: 2, wordBreak: "break-all" }}>{m.baseUrl}</Typography>
                <Typography variant="caption" color="text.secondary">Sub-módulos</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                  {m.subModules.map((sm) => <Chip key={sm.id} label={sm.name} size="small" />)}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <Typography variant="body2"><strong>{m._count.roles}</strong> papéis</Typography>
                    <Typography variant="body2"><strong>{m._count.licenseTypes}</strong> tipos de licença</Typography>
                  </Stack>
                  <Button size="small" startIcon={<VerifiedIcon />} onClick={() => setTypesDlg(m)}>
                    Tipos de licença
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {typesDlg && (
        <LicenseTypesDialog
          key={typesDlg.id}
          module={typesDlg}
          onClose={() => setTypesDlg(null)}
          isMaster={isMaster}
        />
      )}

      {dlg.open && (
        <ModuleDialog
          key={dlg.row?.id ?? "new"}
          state={dlg}
          onClose={() => setDlg({ open: false })}
          onSubmit={(v) => {
            if (dlg.row) updateM.mutate({ id: dlg.row.id, v });
            else createM.mutate(v);
          }}
          submitting={createM.isPending || updateM.isPending}
        />
      )}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Desativar módulo"
        description={`Confirmar desativação de "${confirmDelete?.name}"? Licenças existentes serão preservadas, mas o módulo deixará de aparecer.`}
        confirmLabel="Desativar"
        severity="error"
        loading={deleteM.isPending}
        onConfirm={() => { if (confirmDelete) deleteM.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  );
}

function ModuleDialog({ state, onClose, onSubmit, submitting }: {
  state: DialogState; onClose: () => void;
  onSubmit: (v: ModuleFormValues) => void; submitting: boolean;
}) {
  const [v, setV] = useState<ModuleFormValues>(() => ({
    key: state.row?.key ?? "",
    name: state.row?.name ?? "",
    description: state.row?.description ?? "",
    baseUrl: state.row?.baseUrl ?? "",
    webhookUrl: state.row?.webhookUrl ?? "",
    active: state.row?.active ?? true,
  }));
  const isEdit = !!state.row;
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth maxWidth="sm"
      slotProps={{ paper: { component: "form", onSubmit: (e: React.FormEvent) => { e.preventDefault(); onSubmit(v); } } }}
    >
      <DialogTitle>{isEdit ? "Editar módulo" : "Novo módulo"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Chave" required value={v.key} disabled={isEdit}
            onChange={(e) => setV({ ...v, key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
            helperText="a-z, 0-9, hífen (ex.: voluntarios)"
            fullWidth
          />
          <TextField label="Nome" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} fullWidth />
          <TextField label="Descrição" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} fullWidth multiline rows={2} />
          <TextField label="URL base" required value={v.baseUrl} onChange={(e) => setV({ ...v, baseUrl: e.target.value })} fullWidth />
          <TextField label="Webhook URL (opcional)" value={v.webhookUrl} onChange={(e) => setV({ ...v, webhookUrl: e.target.value })} fullWidth />
          <FormControlLabel
            control={<Switch checked={v.active} onChange={(_, val) => setV({ ...v, active: val })} />}
            label="Ativo"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={submitting}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Diálogo dedicado para gerenciar Tipos de Licença de um módulo
// ───────────────────────────────────────────────────────────────────────────
interface LicenseType {
  id: string;
  moduleId: string;
  kind: "free" | "trial" | "monthly";
  name: string;
  description: string | null;
  trialDays: number | null;
  monthlyPrice: string | null;
  maxUsers: number | null;
  freeRequiresApproval: boolean;
  active: boolean;
}

interface TypeFormValues {
  kind: "free" | "trial" | "monthly";
  name: string;
  description: string;
  trialDays: number;
  monthlyPrice: string;
  maxUsers: string; // "" = ilimitado
  freeRequiresApproval: boolean;
  active: boolean;
}

const KIND_LABEL: Record<string, string> = { free: "Gratuita", trial: "Trial", monthly: "Mensal" };

function LicenseTypesDialog({ module, onClose, isMaster }: { module: ModuleRow; onClose: () => void; isMaster: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<LicenseType | null | "new">(null);
  const [confirmDelete, setConfirmDelete] = useState<LicenseType | null>(null);

  const { data: types, isLoading } = useQuery({
    queryKey: ["license-types", module.id],
    queryFn: async () => (await api.get<LicenseType[]>("/license-types", { params: { module_id: module.id, include_inactive: "true" } })).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["license-types", module.id] });
    qc.invalidateQueries({ queryKey: ["modules"] }); // _count.licenseTypes
  };
  const createM = useMutation({
    mutationFn: (v: TypeFormValues) => api.post("/license-types", payloadFromForm(module.id, v)),
    onSuccess: () => { invalidate(); setEditing(null); },
  });
  const updateM = useMutation({
    mutationFn: ({ id, v }: { id: string; v: TypeFormValues }) => api.patch(`/license-types/${id}`, payloadFromForm(module.id, v)),
    onSuccess: () => { invalidate(); setEditing(null); },
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.delete(`/license-types/${id}`),
    onSuccess: () => { invalidate(); setConfirmDelete(null); },
  });

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Tipos de licença — {module.name}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Cadastre as opções de precificação. Cada licença concedida a uma instituição usa um destes tipos.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isMaster && !editing && (
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setEditing("new")} sx={{ mb: 2 }}>
            Novo tipo
          </Button>
        )}
        {editing && (
          <TypeForm
            key={editing === "new" ? "new" : editing.id}
            initial={editing === "new" ? null : editing}
            onCancel={() => setEditing(null)}
            onSubmit={(v) => editing === "new" ? createM.mutate(v) : updateM.mutate({ id: editing.id, v })}
            submitting={createM.isPending || updateM.isPending}
          />
        )}
        {!editing && (
          <Stack spacing={1}>
            {isLoading && <Typography variant="body2" color="text.secondary">Carregando…</Typography>}
            {!isLoading && (types ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary">Nenhum tipo de licença cadastrado.</Typography>
            )}
            {(types ?? []).map((t) => (
              <Card key={t.id} variant="outlined" sx={{ opacity: t.active ? 1 : 0.6 }}>
                <CardContent sx={{ pb: "16px !important" }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle1">{t.name}</Typography>
                        <Chip size="small" label={KIND_LABEL[t.kind]} color={t.kind === "monthly" ? "primary" : t.kind === "trial" ? "warning" : "default"} />
                        {!t.active && <Chip size="small" label="Inativo" />}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">{t.description ?? "—"}</Typography>
                      <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                        {t.kind === "monthly" && t.monthlyPrice !== null && (
                          <Typography variant="body2"><strong>{formatCurrency(Number(t.monthlyPrice))}</strong>/mês</Typography>
                        )}
                        {t.kind === "trial" && <Typography variant="body2"><strong>{t.trialDays ?? 30}</strong> dias de trial</Typography>}
                        {t.kind === "free" && (
                          <Typography variant="body2">
                            Gratuita{t.freeRequiresApproval ? " · requer aprovação" : " · auto-aprovada"}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {t.maxUsers ? `Até ${t.maxUsers} usuários` : "Usuários ilimitados"}
                        </Typography>
                      </Stack>
                    </Box>
                    {isMaster && (
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(t)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Desativar"><IconButton size="small" color="error" onClick={() => setConfirmDelete(t)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Desativar tipo de licença"
        description={`Desativar "${confirmDelete?.name}"? Licenças já concedidas continuam valendo. Não é possível desativar se houver licenças ativas usando este tipo.`}
        confirmLabel="Desativar" severity="error" loading={deleteM.isPending}
        onConfirm={() => { if (confirmDelete) deleteM.mutate(confirmDelete.id); }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Dialog>
  );
}

function payloadFromForm(moduleId: string, v: TypeFormValues) {
  return {
    moduleId,
    kind: v.kind,
    name: v.name.trim(),
    description: v.description.trim() || undefined,
    trialDays: v.kind === "trial" ? Number(v.trialDays) || 30 : undefined,
    monthlyPrice: v.kind === "monthly" ? Number(v.monthlyPrice) || 0 : undefined,
    maxUsers: v.maxUsers ? Number(v.maxUsers) : undefined,
    freeRequiresApproval: v.kind === "free" ? v.freeRequiresApproval : undefined,
    active: v.active,
  };
}

function TypeForm({ initial, onCancel, onSubmit, submitting }: {
  initial: LicenseType | null;
  onCancel: () => void;
  onSubmit: (v: TypeFormValues) => void;
  submitting: boolean;
}) {
  const [v, setV] = useState<TypeFormValues>({
    kind: initial?.kind ?? "monthly",
    name: initial?.name ?? "",
    description: initial?.description ?? "",
    trialDays: initial?.trialDays ?? 30,
    monthlyPrice: initial?.monthlyPrice ?? "",
    maxUsers: initial?.maxUsers != null ? String(initial.maxUsers) : "",
    freeRequiresApproval: initial?.freeRequiresApproval ?? true,
    active: initial?.active ?? true,
  });
  return (
    <Card variant="outlined" component="form" sx={{ mb: 2 }} onSubmit={(e: React.FormEvent) => { e.preventDefault(); onSubmit(v); }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1">{initial ? "Editar tipo" : "Novo tipo"}</Typography>
          <Stack direction="row" spacing={2}>
            <TextField select label="Modalidade" required value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value as TypeFormValues["kind"] })} sx={{ width: 200 }}>
              <MenuItem value="free">Gratuita</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="monthly">Mensal</MenuItem>
            </TextField>
            <TextField label="Nome" required value={v.name} onChange={(e) => setV({ ...v, name: e.target.value })} fullWidth />
          </Stack>
          <TextField label="Descrição" value={v.description} onChange={(e) => setV({ ...v, description: e.target.value })} fullWidth multiline rows={2} />
          {v.kind === "monthly" && (
            <TextField
              label="Preço mensal (BRL)" type="number" required
              inputProps={{ min: 0, step: 0.01 }}
              value={v.monthlyPrice} onChange={(e) => setV({ ...v, monthlyPrice: e.target.value })}
              fullWidth
            />
          )}
          {v.kind === "trial" && (
            <TextField
              label="Dias do trial" type="number" required
              inputProps={{ min: 1, max: 365 }}
              value={v.trialDays} onChange={(e) => setV({ ...v, trialDays: Number(e.target.value) })}
              helperText="Padrão sugerido: 30 dias"
              fullWidth
            />
          )}
          {v.kind === "free" && (
            <FormControlLabel
              control={<Switch checked={v.freeRequiresApproval} onChange={(_, val) => setV({ ...v, freeRequiresApproval: val })} />}
              label="Requer aprovação de administrador para ativar"
            />
          )}
          <TextField
            label="Máximo de usuários" type="number"
            inputProps={{ min: 1 }}
            value={v.maxUsers} onChange={(e) => setV({ ...v, maxUsers: e.target.value })}
            helperText="Deixe vazio para usuários ilimitados"
            fullWidth
          />
          <FormControlLabel
            control={<Switch checked={v.active} onChange={(_, val) => setV({ ...v, active: val })} />}
            label="Tipo ativo (aparece nas opções de concessão)"
          />
          <Stack direction="row" spacing={1}>
            <Button type="submit" variant="contained" disabled={submitting || !v.name.trim()}>Salvar</Button>
            <Button onClick={onCancel}>Cancelar</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
